<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Legend,
} from 'chart.js';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const { t } = useI18n();

ChartJS.register(
  Title,
  Tooltip,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Legend
);

const fontFamily =
  'Inter,-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

const chartData = computed(() => ({
  labels: props.rows.map(row => row.date),
  datasets: [
    {
      label: t('CSAT_REPORTS.DYNAMICS.AVERAGE_RATING'),
      data: props.rows.map(row => row.average_rating),
      borderColor: '#6366f1',
      backgroundColor: '#6366f1',
      yAxisID: 'y',
      tension: 0.3,
      spanGaps: true,
    },
    {
      label: t('CSAT_REPORTS.DYNAMICS.RESPONSE_RATE'),
      data: props.rows.map(row => row.response_rate),
      borderColor: '#22c55e',
      backgroundColor: '#22c55e',
      yAxisID: 'y1',
      tension: 0.3,
      spanGaps: true,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: { fontFamily, boxWidth: 12 },
    },
    tooltip: {
      callbacks: {
        label: context => {
          if (context.dataset.yAxisID === 'y1') {
            const value =
              context.parsed.y === null || context.parsed.y === undefined
                ? '—'
                : context.parsed.y;
            return `${context.dataset.label}: ${value}%`;
          }
          const value =
            context.parsed.y === null || context.parsed.y === undefined
              ? '—'
              : context.parsed.y;
          return `${context.dataset.label}: ${value}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: { fontFamily },
      grid: { drawOnChartArea: false },
    },
    y: {
      type: 'linear',
      position: 'left',
      min: 0,
      max: 5,
      title: {
        display: true,
        text: t('CSAT_REPORTS.DYNAMICS.AVERAGE_RATING'),
        font: { family: fontFamily },
      },
      ticks: { fontFamily },
      grid: { drawOnChartArea: false },
    },
    y1: {
      type: 'linear',
      position: 'right',
      min: 0,
      max: 100,
      title: {
        display: true,
        text: t('CSAT_REPORTS.DYNAMICS.RESPONSE_RATE'),
        font: { family: fontFamily },
      },
      ticks: {
        fontFamily,
        callback: value => `${value}%`,
      },
      grid: { drawOnChartArea: false },
    },
  },
}));
</script>

<template>
  <section
    class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="mb-4 text-base font-medium text-n-slate-12">
      {{ $t('CSAT_REPORTS.DYNAMICS.TITLE') }}
    </h3>
    <div v-if="isLoading" class="h-72 animate-pulse rounded-lg bg-n-alpha-2" />
    <div v-else-if="rows.length" class="h-72">
      <Line :data="chartData" :options="chartOptions" />
    </div>
    <p v-else class="py-16 text-center text-sm text-n-slate-11">
      {{ $t('CSAT_REPORTS.DYNAMICS.EMPTY') }}
    </p>
  </section>
</template>
